/**
 * Высота каркаса приложения — измеренная, а не вычисленная в единицах CSS.
 *
 * История вопроса. Сначала каркас жил на `100dvh`: в iOS «на экран» при первом
 * рендере это оказывалось меньше настоящего экрана, нижняя панель вставала
 * выше низа, и только свайп заставлял Safari пересчитать. Замена на
 * `position: fixed; inset: 0` сделала хуже: панель осталась приподнятой уже
 * навсегда — значит и область фиксированного слоя там меньше экрана.
 *
 * Поэтому высоту не угадываем формулой, а спрашиваем у браузера и кладём в
 * `--app-h`. Берём наибольшее из того, что он сообщает: `visualViewport`
 * занижает высоту, когда открыта клавиатура или тянется панель, а
 * `innerHeight` в standalone совпадает с экраном.
 */
/**
 * Открыта ли клавиатура.
 *
 * Сравниваем видимую область с ЭКРАНОМ, а не с окном. Замеры на устройстве
 * (iPhone, установленное приложение) объясняют почему:
 *
 * | | до клавиатуры | с клавиатурой |
 * | --- | --- | --- |
 * | `screen.height`      | 932 | 932 |
 * | `innerHeight`        | 932 | 627 |
 * | `visualViewport`     | 932 | 546 |
 *
 * Окно ужимается вместе с клавиатурой, поэтому разница `innerHeight − visual`
 * составляет всего 81 — по ней клавиатуру не отличить от вытянутой панели
 * браузера, и прежний порог в 120 не срабатывал ни разу. А от экрана видимая
 * область отстаёт на 386, то есть заметно больше четверти: вот это и признак.
 */
function keyboardOpen(): boolean {
  const visual = window.visualViewport?.height ?? 0
  const screenH = window.screen?.height ?? 0
  return visual > 0 && screenH > 0 && visual < screenH * 0.75
}

const setHeight = () => {
  const visual = window.visualViewport?.height ?? 0
  // С клавиатурой каркас равен видимой области, а не окну: окно (627) выше
  // того, что видно (546), и нижний край каркаса вместе с полем ввода ушёл бы
  // под панель подсказок клавиатуры.
  const reported = keyboardOpen() ? visual : Math.max(visual, window.innerHeight || 0)
  if (reported <= 0) return

  // Полоса под строку статуса нужна, только когда каркас стоит у низа экрана.
  const gap = keyboardOpen() ? 0 : iosGap(reported)
  // Каркас делаем выше окна ровно на недостающую полосу и сразу прокручиваем
  // страницу на неё же. Это то самое, чего раньше приходилось добиваться
  // свайпом: iOS «на экран» сообщает высоту без строки статуса, и нижняя
  // панель не доставала до края экрана.
  document.documentElement.style.setProperty('--app-h', `${Math.round(reported + gap)}px`)
  document.documentElement.style.setProperty('--ios-gap', `${Math.round(gap)}px`)
  if (gap > 0) window.scrollTo(0, gap)
  // С клавиатурой — в ноль: см. `keepScrolled`, там же и замеры.
  else if (keyboardOpen()) window.scrollTo(0, 0)
}

/**
 * Насколько окно приложения ниже экрана на iPhone «на экран».
 *
 * Замеры на устройстве: окно 873, экран 932, `safe-area-inset-top` 59 — ровно
 * строка статуса. В остальных случаях (браузер, Android, Telegram) возвращаем
 * ноль и ничего не трогаем.
 */
function iosGap(reported: number): number {
  const screenH = window.screen?.height ?? 0
  const standalone = window.matchMedia('(display-mode: standalone)').matches
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
  if (!ios || !standalone || screenH <= 0) return 0

  const missing = screenH - reported
  if (missing <= 0 || missing > 80) return 0

  const inset = topInset()
  if (inset > 0 && Math.abs(missing - inset) > 4) return 0
  return missing
}

/** Величина `env(safe-area-inset-top)` в пикселях — её иначе не прочитать. */
function topInset(): number {
  const probe = document.createElement('div')
  probe.style.cssText = 'position:absolute;visibility:hidden;padding-top:env(safe-area-inset-top,0px)'
  document.body.appendChild(probe)
  const inset = parseFloat(getComputedStyle(probe).paddingTop) || 0
  probe.remove()
  return inset
}



export function watchViewportHeight(): void {
  setHeight()
  // Первые кадры iOS ещё «доезжает»: меряем ещё раз, когда раскладка устоялась.
  requestAnimationFrame(setHeight)
  setTimeout(setHeight, 300)

  pinFrame()
  // iOS отматывает прокрутку назад после жестов и возврата из фона.
  window.addEventListener('scroll', keepScrolled, { passive: true })
  window.addEventListener('resize', setHeight)
  window.addEventListener('orientationchange', setHeight)
  // Смена вкладки и возврат из фона на iOS тоже меняют высоту.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) setHeight()
  })
  window.visualViewport?.addEventListener('resize', setHeight)
  // Когда iOS двигает видимую область, а не прокручивает документ, обычный
  // `scroll` на окне не приходит — возвращать каркас на место было бы некому.
  window.visualViewport?.addEventListener('scroll', keepScrolled)
}

/**
 * Возвращает страницу в положение, при котором каркас упирается в низ экрана.
 *
 * С клавиатурой это положение — ноль, и вернуть его обязательно. Замер на
 * устройстве: iOS успевает отмотать страницу на 246 пикселей, подтягивая поле
 * ввода, ПОКА окно ещё во весь экран и прокручивать есть куда. Потом окно
 * ужимается, прокручивать становится нечего, а те 246 остаются — и каркас
 * висит сдвинутым (`top -246`), пока его не вернут свайпом.
 *
 * Прежнее условие `gap > 0` этот случай пропускало целиком: полосы под строку
 * статуса на устройстве не оказалось, `gap` был нулевым, и сторож не работал
 * ни разу.
 */
function keepScrolled(): void {
  const gap = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--ios-gap'),
  ) || 0
  const target = keyboardOpen() ? 0 : gap
  if ((target > 0 || keyboardOpen()) && Math.abs(window.scrollY - target) > 1) {
    window.scrollTo(0, target)
  }
}

/**
 * Держит каркас неподвижным.
 *
 * `.app` объявлен `overflow: hidden`, но это не запрещает прокручивать его
 * программно — а браузер именно это и делает, подтягивая в видимую область
 * поле ввода, на которое встал фокус. Прокручивался весь каркас разом, вместе
 * с шапкой, и вернуть его было нечем: обработчик выше следит за прокруткой
 * окна, а не каркаса. Здесь мы её просто отменяем: содержимое приложения
 * прокручивает `.app__body`, а рамка обязана стоять на месте.
 */
function pinFrame(): void {
  // Слушаем на документе с перехватом: `scroll` не всплывает, а сам каркас
  // к моменту вызова ещё не отрисован — React рендерит позже.
  document.addEventListener(
    'scroll',
    (e) => {
      const el = e.target
      if (!(el instanceof HTMLElement) || !el.classList.contains('app')) return
      if (el.scrollTop !== 0) el.scrollTop = 0
      if (el.scrollLeft !== 0) el.scrollLeft = 0
    },
    { capture: true, passive: true },
  )
}

/**
 * Показывает поле ввода, когда его накрыла клавиатура.
 *
 * Вешается на `onFocus` любого поля, которое лежит низко на длинной странице.
 * Работа тут вся в том, ЧТО именно двигать: прокручивается только лента
 * (`.app__body`), и только на столько, на сколько поле ушло за нижний край.
 *
 * **`scrollIntoView` использовать нельзя** — он прокручивает всех
 * прокручиваемых предков разом, включая каркас, а каркас двигаться не должен
 * (его держит `pinFrame`, и уехавший каркас потом нечем вернуть).
 *
 * Двигаем дважды, с задержкой: клавиатура выезжает анимацией, каркас ужимается
 * не сразу, и в первый момент видимая область ещё старая. Считаем по
 * измеренным прямоугольникам, а не по `scrollHeight`: поле не обязано быть
 * последним на странице — под ним бывает и кнопка отправки, и подпись про
 * остаток вопросов.
 */
export function revealField(field: HTMLElement): void {
  for (const ms of [120, 350]) {
    setTimeout(() => {
      const body = document.querySelector<HTMLElement>('.app__body')
      if (!body || !field.isConnected) return
      // Запас снизу — чтобы под полем осталась видна кнопка отправки, а не
      // ровно нижняя граница самого поля.
      const gap = field.getBoundingClientRect().bottom + 76 - body.getBoundingClientRect().bottom
      if (gap > 0) body.scrollTop += gap
    }, ms)
  }
}
